import React, { useEffect, useState } from "react";
import { CheckCircle2, Clock3, XCircle } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { get } from "../../lib/api";

const PaymentResult = () => {
  const [searchParams] = useSearchParams();
  const paymentId = searchParams.get("payment_id");
  const [payment, setPayment] = useState(null);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const [waiting, setWaiting] = useState(false);

  useEffect(() => {
    let active = true;
    let timer;
    let attempts = 0;
    setPayment(null);
    setError("");
    setWaiting(false);

    const checkStatus = async () => {
      if (!paymentId) {
        setError("The payment reference is missing.");
        return;
      }
      try {
        const response = await get(`/payments/${paymentId}/status`);
        if (!active) return;
        setPayment(response.payment);
        setError("");
        if (response.payment.status === "pending" && attempts < 20) {
          attempts += 1;
          timer = setTimeout(checkStatus, 2000);
        } else if (response.payment.status === "pending") {
          setWaiting(true);
        }
      } catch (err) {
        if (active)
          setError(err.response?.data?.message || "Unable to verify payment.");
      }
    };

    checkStatus();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [paymentId, revision]);

  const paid = payment?.status === "paid";
  const failed = ["failed", "cancelled"].includes(payment?.status);
  const refunded = payment?.status === "refunded";

  return (
    <div className="mx-auto max-w-lg py-12">
      <Card>
        <CardContent className="space-y-5 p-8 text-center">
          {paid ? (
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
          ) : failed || error ? (
            <XCircle className="mx-auto h-14 w-14 text-red-500" />
          ) : (
            <Clock3 className="mx-auto h-14 w-14 animate-pulse text-amber-500" />
          )}
          <div>
            <h1 className="text-2xl font-bold">
              {paid
                ? payment.refundReviewRequired ? "Payment needs refund review" : "Payment confirmed"
                : refunded
                  ? "Payment refunded"
                : error
                  ? "Unable to check payment"
                : failed || error
                  ? "Payment not completed"
                  : "Confirming your payment"}
            </h1>
            <p className="mt-2 text-sm text-gray-500">
              {paid
                ? payment.refundReviewRequired ? "Payment was received for a cancelled order. The order remains cancelled. Contact support for refund review." : "Your order has been paid and sent to the vendor."
                : refunded
                  ? "This payment has been refunded. View your orders for details."
                : waiting
                  ? "Payment confirmation is taking longer than expected. Check again or view your orders before making another payment."
                : failed || error
                  ? error ||
                    payment?.failureReason ||
                    "Please try checking out again."
                  : `${payment?.provider === "MAYA" ? "Maya" : payment?.provider === "PAYMONGO" ? "PayMongo" : "Your payment provider"} is securely confirming the transaction. This usually takes only a moment.`}
            </p>
          </div>
          {payment && (
            <p className="text-sm font-semibold">
              Total: PHP {Number(payment.amount).toFixed(2)}
            </p>
          )}
          <div className="flex justify-center gap-3">
            {paymentId && (error || waiting) && <Button variant="outline" onClick={() => setRevision(value => value + 1)}>Check again</Button>}
            <Link to="/orders">
              <Button>View orders</Button>
            </Link>
            {(failed || error) && (
              <Link to="/cart">
                <Button variant="outline">Return to cart</Button>
              </Link>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentResult;
